import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, RefreshCw } from "lucide-react";
import { S, MANROPE, FOCUS, API, auDate } from "./lib";

// Prospects worth an email. Drafting one from here opens it straight away.
export default function OutreachQueue({ onDrafted }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/needs-touch`, { params: { limit: 50 } });
      setRows(data.prospects || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the queue");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const draft = async (p) => {
    setBusy(p.prospect_id);
    try {
      const { data } = await axios.post(`${API}/emails`, {
        prospect_id: p.prospect_id, intent: p.why,
      });
      toast.success(`${data.email.ref} started`);
      onDrafted?.(data.email.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start that draft");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-[11px] overflow-hidden" data-testid="outreach-queue"
      style={{ background: S.card, border: `1px solid ${S.border}` }}>
      <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${S.divider}` }}>
        <div className="text-[13px] font-bold pointer-events-none" style={{ ...MANROPE, color: S.ink }}>
          Worth an email
        </div>
        <span className="mono text-[11px] pointer-events-none" style={{ color: S.ink3 }}>{rows.length}</span>
        <button onClick={load} data-testid="outreach-queue-refresh"
          className={`ml-auto h-8 px-2 rounded-[6px] inline-flex items-center gap-1 text-[10.5px] font-bold uppercase ${FOCUS}`}
          style={{ background: S.panel, border: `1px solid ${S.border}`, color: S.ink2 }}>
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {loading && <div className="px-4 py-6 text-[12px]" style={{ color: S.ink3 }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-6 text-[12px]" style={{ color: S.ink3 }}>
            Nothing due. Every prospect with an open follow-up already has a draft or has been emailed.
          </div>
        )}
        {rows.map((p) => (
          <div key={p.prospect_id} data-testid={`outreach-queue-row-${p.prospect_id}`}
            className="px-4 py-2.5 flex items-center gap-3" style={{ borderBottom: `1px solid ${S.divider}` }}>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold truncate" style={{ color: S.ink }}>
                {p.company_name || "—"}
              </div>
              <div className="text-[11px] truncate" style={{ color: S.ink3 }}>
                {p.contact_name || "No contact"} · {p.why}
                {p.next_action_date ? ` · due ${auDate(p.next_action_date)}` : ""}
                {p.assigned_rep ? ` · ${p.assigned_rep}` : ""}
              </div>
            </div>
            <button onClick={() => draft(p)} disabled={busy === p.prospect_id}
              data-testid={`outreach-queue-draft-${p.prospect_id}`}
              className={`h-8 px-2.5 rounded-[6px] inline-flex items-center gap-1 text-[10.5px] font-bold uppercase disabled:opacity-50 ${FOCUS}`}
              style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000", ...MANROPE }}>
              <PenLine className="w-3 h-3" /> Draft
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
